import { Head, useForm } from "@inertiajs/react"

import LocationSelect from "@/components/location-select"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import type RequestsSetupClubProfile from "@/types/serializers/RequestsSetupClubProfile"

export default function ClubSetup() {
  const { data, setData, post, processing, errors } =
    useForm<RequestsSetupClubProfile>({
      club_profile: {
        name: "",
        description: "",
        country: "",
        region: "",
        city: "",
        division: "",
        founded_year: null,
        website_url: "",
      },
    })

  const setField = (
    field: keyof typeof data.club_profile,
    value: string | number | null,
  ) =>
    setData("club_profile", {
      ...data.club_profile,
      [field]: value,
    } as typeof data.club_profile)

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    post("/onboarding/club_setup")
  }

  return (
    <>
      <Head title="Completar perfil del Club" />

      <div className="bg-background flex min-h-svh flex-col items-center p-6">
        <img
          src="/images/krown_logo_header.png"
          alt="KrownFT"
          className="mb-6 h-10"
        />

        <div className="w-full max-w-lg">
          <div className="mb-6 text-center">
            <h1 className="text-xl font-semibold text-white">
              Completar perfil del Club
            </h1>
            <p className="text-krown-50/70 mt-1 text-sm">
              Completá la información de tu club para empezar a buscar talento
            </p>
          </div>

          <form
            onSubmit={handleSubmit}
            className="bg-card space-y-4 rounded-lg p-6 shadow-lg"
          >
            <div className="space-y-1.5">
              <Label htmlFor="name">Nombre del club</Label>
              <Input
                id="name"
                value={data.club_profile.name}
                onChange={(e) => setField("name", e.target.value)}
                placeholder="Ej: Club Atlético Rosario Central"
              />
              {errors["club_profile.name"] && (
                <p className="mt-1 text-sm text-red-500">
                  {errors["club_profile.name"]}
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="description">Descripción</Label>
              <Textarea
                id="description"
                value={data.club_profile.description ?? ""}
                onChange={(e) => setField("description", e.target.value)}
                placeholder="Contá un poco sobre el club..."
                rows={4}
              />
            </div>

            <LocationSelect
              country={data.club_profile.country}
              region={data.club_profile.region ?? ""}
              city={data.club_profile.city ?? ""}
              onCountryChange={(v) =>
                setData("club_profile", {
                  ...data.club_profile,
                  country: v,
                  region: "",
                  city: "",
                })
              }
              onRegionChange={(v) =>
                setData("club_profile", {
                  ...data.club_profile,
                  region: v,
                  city: "",
                })
              }
              onCityChange={(v) => setField("city", v)}
              errors={{ country: errors["club_profile.country"]?.[0] }}
            />

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="division">División</Label>
                <Input
                  id="division"
                  value={data.club_profile.division ?? ""}
                  onChange={(e) => setField("division", e.target.value)}
                  placeholder="Ej: Primera División"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="founded_year">Año de fundación</Label>
                <Input
                  id="founded_year"
                  type="number"
                  value={data.club_profile.founded_year ?? ""}
                  onChange={(e) => setField("founded_year", e.target.value)}
                  placeholder="Ej: 1905"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="website_url">Sitio web</Label>
              <Input
                id="website_url"
                type="url"
                value={data.club_profile.website_url ?? ""}
                onChange={(e) => setField("website_url", e.target.value)}
                placeholder="https://www.miclub.com"
              />
            </div>

            <div className="pt-2">
              <Button
                type="submit"
                disabled={processing}
                className="bg-krown-accent text-krown-800 hover:bg-krown-accent/90 w-full"
              >
                Completar perfil
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  )
}
